import './otp-input.scss';

import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';

const OTPInput = forwardRef(({ length = 6, onChange, error, disabled }, ref) => {
  const [values, setValues] = useState(Array(length).fill(''));
  const inputRefs = useRef([]);

  useEffect(() => {
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      fill: (text) => {
        const digits = text.replace(/\D/g, '').slice(0, length);
        if (!digits) return;
        const newValues = Array(length).fill('');
        digits.split('').forEach((char, i) => {
          newValues[i] = char;
        });
        setValues(newValues);
        onChange(newValues.join(''));
        const nextIndex = Math.min(digits.length, length - 1);
        inputRefs.current[nextIndex]?.focus();
      }
    }),
    [length, onChange]
  );

  const handleChange = (index, e) => {
    const value = e.target.value;

    if (!/^\d*$/.test(value)) {
      return;
    }

    const newValues = [...values];

    if (value.length > 1) {
      const pastedValues = value.slice(0, length).split('');
      pastedValues.forEach((char, i) => {
        if (index + i < length) {
          newValues[index + i] = char;
        }
      });
      setValues(newValues);
      onChange(newValues.join(''));

      const nextIndex = Math.min(index + pastedValues.length, length - 1);
      inputRefs.current[nextIndex]?.focus();
      return;
    }

    newValues[index] = value;
    setValues(newValues);
    onChange(newValues.join(''));

    if (value && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleClick = (index) => {
    inputRefs.current[index]?.select();
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !values[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, length);

    if (pastedData) {
      const newValues = Array(length).fill('');
      pastedData.split('').forEach((char, i) => {
        newValues[i] = char;
      });
      setValues(newValues);
      onChange(newValues.join(''));

      const nextIndex = Math.min(pastedData.length, length - 1);
      inputRefs.current[nextIndex]?.focus();
    }
  };

  return (
    <div className="otp-input">
      <div className="otp-input__fields">
        {values.map((value, index) => (
          <input
            key={index}
            ref={(el) => (inputRefs.current[index] = el)}
            type="password"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={length}
            value={value}
            onClick={() => handleClick(index)}
            onChange={(e) => handleChange(index, e)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            onPaste={handlePaste}
            disabled={disabled}
            className={classNames('otp-input__field', {
              'otp-input__field--error': error,
              'otp-input__field--filled': value
            })}
            aria-label={`Digit ${index + 1}`}
            data-testid={`otp-input-${index}`}
          />
        ))}
      </div>
      {error && <p className="otp-input__error">{error}</p>}
    </div>
  );
});

OTPInput.displayName = 'OTPInput';

OTPInput.propTypes = {
  length: PropTypes.number,
  onChange: PropTypes.func.isRequired,
  error: PropTypes.string,
  disabled: PropTypes.bool
};

export { OTPInput };
